/**
 * geminiTransport.ts
 * Authenticated HTTP transport to the server-side Gemini proxy.
 */

import { auth } from '../src/firebaseConfig';
import { isLongRunningGeneratePolicy, resolveGenerateUrl } from '../src/utils/apiBase';
import type { GenerationErrorBody } from '../src/types';
import { getActiveGenerationRunId } from '../src/utils/generationRunContext';

export class RateLimitError extends Error {
  retryAfterSeconds: number;
  constructor(message: string, retryAfterSeconds = 60) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export class GenerationOutcomeUnknownError extends Error {
  readonly code = 'TIMEOUT_UNKNOWN_OUTCOME';
  readonly retryable = true;
  readonly outcomeKnown = false;
  requestId?: string;
  operation?: string;
  agent?: string;

  constructor(message: string, meta?: { requestId?: string; operation?: string; agent?: string }) {
    super(message);
    this.name = 'GenerationOutcomeUnknownError';
    this.requestId = meta?.requestId;
    this.operation = meta?.operation;
    this.agent = meta?.agent;
  }
}

export type GenerateTransportPolicy =
  | 'standard'
  | 'sequel_step'
  | 'major_scene'
  | 'four_beat_initial'
  | 'four_beat_repair'
  | 'original_engine_compat';

export class GenerationTransportError extends Error {
  readonly requestId?: string;
  readonly logicalCallId?: string;
  readonly generationPolicy?: string;
  readonly operation?: string;
  readonly httpStatus?: number;
  readonly failurePhase: 'gateway' | 'network' | 'timeout' | 'server' | 'client_abort';
  readonly outcomeKnown: boolean;

  constructor(
    message: string,
    meta: {
      requestId?: string;
      logicalCallId?: string;
      generationPolicy?: string;
      operation?: string;
      httpStatus?: number;
      failurePhase: GenerationTransportError['failurePhase'];
      outcomeKnown: boolean;
    },
  ) {
    super(message);
    this.name = 'GenerationTransportError';
    Object.assign(this, meta);
  }
}

export interface GenerateTransportOptions {
  policy?: GenerateTransportPolicy;
  timeoutMs?: number;
  maxRetries?: number;
  operation?: string;
  agent?: string;
  runId?: string;
  projectId?: string;
  logicalCallId?: string;
}

const MAX_STANDARD_RETRIES = 5;
const BASE_DELAY_MS = 3_000;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

let _cachedToken: string | null = null;
let _tokenExpiresAt = 0;

const getIdToken = async (forceRefresh = false): Promise<string> => {
  if (!forceRefresh && _cachedToken && Date.now() < _tokenExpiresAt) return _cachedToken;

  const user = auth.currentUser;
  if (!user) throw new Error('[geminiTransport] No signed-in user. Cannot call /api/generate.');

  const token = await user.getIdToken(forceRefresh);
  _cachedToken = token;
  _tokenExpiresAt = Date.now() + 50 * 60 * 1_000;
  return token;
};

if (typeof window !== 'undefined') {
  auth.onAuthStateChanged((user) => {
    if (!user) {
      _cachedToken = null;
      _tokenExpiresAt = 0;
    }
  });
}

export interface ServerGenerateResponse {
  text: string;
  requestId?: string;
  finishReason?: string;
  outputTokenCount?: number;
}

export let lastRequestId: string | undefined = undefined;

function resolveAgentLabel(params: Record<string, unknown>, explicit?: string): string {
  if (explicit) return explicit;
  const config = params.config as { systemInstruction?: string } | undefined;
  const instruction = config?.systemInstruction;
  if (typeof instruction === 'string' && instruction.length > 0) {
    if (instruction.includes('ARCHIVIST')) return 'Archivist';
    if (instruction.includes('ARCHITECT')) return 'Architect';
    if (instruction.includes('SCRIBE')) return 'Scribe';
    return instruction.slice(0, 40).replace(/\s+/g, ' ');
  }
  return 'Unknown';
}

function parseErrorBody(rawBody: string): Partial<GenerationErrorBody> {
  try {
    return JSON.parse(rawBody) as Partial<GenerationErrorBody>;
  } catch {
    return {};
  }
}

function isGatewayStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

function logTransportFailure(diagnostic: Record<string, unknown>): void {
  console.error(`[geminiTransport] ${JSON.stringify(diagnostic)}`);
}

export const serverGenerateWithRetry = async (
  params: Record<string, unknown>,
  options: GenerateTransportOptions = {},
): Promise<ServerGenerateResponse> => {
  const policy = options.policy ?? 'standard';
  const longRunning = isLongRunningGeneratePolicy(policy);
  const isMajorScene = policy === 'major_scene';
  const isFourBeatPolicy = policy === 'four_beat_initial' || policy === 'four_beat_repair';
  const isOriginalCompat = policy === 'original_engine_compat';
  const generateUrl = resolveGenerateUrl(longRunning);
  const timeoutMs = options.timeoutMs ?? (policy === 'sequel_step' ? 58_000 : 240_000);
  const maxRetries =
    options.maxRetries ??
    (policy === 'sequel_step' || isMajorScene || isFourBeatPolicy || isOriginalCompat ? 1 : MAX_STANDARD_RETRIES);
  const agent = resolveAgentLabel(params, options.agent);
  const operation = options.operation;
  const logicalCallId = options.logicalCallId;
  const runId = options.runId || getActiveGenerationRunId() || undefined;
  const projectId = options.projectId;

  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const token = await getIdToken(attempt > 0);

      const controller = new AbortController();
      const timerId = setTimeout(() => controller.abort(), timeoutMs);

      const requestBody = {
        ...(projectId || runId ? { ...params, ...(projectId ? { projectId } : {}), ...(runId ? { runId } : {}) } : params),
        ...(isOriginalCompat
          ? { generationPolicy: 'original_engine_compat', serverUpstreamRetryLimit: 1 }
          : {}),
      };

      let res: Response;
      try {
        res = await fetch(generateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...(policy === 'sequel_step' ? { 'X-Generation-Policy': 'sequel' } : {}),
            ...(isMajorScene ? { 'X-Generation-Policy': 'major_scene' } : {}),
            ...(isFourBeatPolicy ? { 'X-Generation-Policy': policy } : {}),
            ...(isOriginalCompat ? { 'X-Generation-Policy': 'original_engine_compat' } : {}),
            ...(operation ? { 'X-Operation': operation } : {}),
            ...(agent ? { 'X-Agent': agent } : {}),
            ...(runId ? { 'X-Run-Id': runId } : {}),
            ...(logicalCallId ? { 'X-Logical-Call-Id': logicalCallId } : {}),
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timerId);
      }

      if (res.status === 429) {
        let requestId = res.headers.get('x-request-id') || undefined;
        let retryAfterSecs = 60;
        const retryHeader = res.headers.get('Retry-After') || res.headers.get('retry-after');
        if (retryHeader) {
          const parsed = parseInt(retryHeader, 10);
          if (!isNaN(parsed) && parsed > 0) retryAfterSecs = parsed;
        }
        const data = parseErrorBody(await res.clone().text());
        if (data.retryable === false) {
          throw new Error(data.error || 'Rate limited');
        }
        if (typeof (data as { retryAfterSeconds?: number }).retryAfterSeconds === 'number') {
          retryAfterSecs = (data as { retryAfterSeconds: number }).retryAfterSeconds;
        }
        if (data.requestId) requestId = data.requestId;
        if (requestId) lastRequestId = requestId;
        throw new RateLimitError(
          `Generation paused because the rate limit was reached. Please wait ${retryAfterSecs} seconds and resume.`,
          retryAfterSecs,
        );
      }

      if (res.status === 401) {
        _cachedToken = null;
        _tokenExpiresAt = 0;
        if (attempt === 0 && policy === 'standard') continue;
        throw new Error('[geminiTransport] Authentication failed after token refresh.');
      }

      if (!res.ok) {
        const rawBody = await res.clone().text();
        const data = parseErrorBody(rawBody);
        const requestId =
          data.requestId || res.headers.get('x-request-id') || res.headers.get('X-Request-Id') || undefined;
        if (requestId) lastRequestId = requestId;

        const errorMessage = data.error || `Server error ${res.status}`;

        if (longRunning && isGatewayStatus(res.status)) {
          logTransportFailure({
            event: 'long_running_gateway_failure',
            requestId: requestId || null,
            logicalCallId: logicalCallId || null,
            generationPolicy: policy,
            operation: operation || null,
            httpStatus: res.status,
            failurePhase: 'gateway',
            generateUrl,
            attempt: attempt + 1,
            maxRetries,
          });
          if (policy === 'sequel_step') {
            throw new GenerationOutcomeUnknownError(
              'Generation service timed out before a definitive result was returned. Resume the sequel setup to continue.',
              { requestId, operation, agent },
            );
          }
          if (requestId) {
            throw new GenerationOutcomeUnknownError(
              'Generation request ended before a definitive result was returned.',
              { requestId, operation, agent },
            );
          }
        }

        if (isGatewayStatus(res.status)) {
          logTransportFailure({
            event: 'gateway_failure',
            requestId: requestId || null,
            logicalCallId: logicalCallId || null,
            generationPolicy: policy,
            operation: operation || null,
            httpStatus: res.status,
            failurePhase: 'gateway',
            generateUrl,
            attempt: attempt + 1,
            maxRetries,
          });
        }

        lastError = new GenerationTransportError(errorMessage, {
          requestId,
          logicalCallId,
          generationPolicy: policy,
          operation,
          httpStatus: res.status,
          failurePhase: 'server',
          outcomeKnown: true,
        });
        const isRetryable = data.retryable ?? res.status >= 500;
        if (isRetryable && attempt < maxRetries - 1) {
          const backoff = BASE_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[geminiTransport] Server error ${res.status} (attempt ${attempt + 1}). Retrying in ${backoff}ms.`);
          await delay(backoff);
          continue;
        }
        throw lastError;
      }

      if (attempt > 0) {
        console.log(`[geminiTransport] Successfully recovered from previous failure on attempt ${attempt + 1}`);
      }

      const data = (await res.json()) as ServerGenerateResponse;
      const requestId = data.requestId || res.headers.get('x-request-id') || undefined;
      lastRequestId = requestId;
      return {
        text: data.text ?? '',
        requestId,
        finishReason: data.finishReason,
        outputTokenCount: data.outputTokenCount,
      };
    } catch (error: unknown) {
      lastError = error;
      if (error instanceof GenerationOutcomeUnknownError || error instanceof RateLimitError) {
        throw error;
      }

      if (error instanceof GenerationTransportError) {
        throw error;
      }

      const err = error as { name?: string; message?: string };
      const isNetworkError =
        err.name === 'AbortError' ||
        err.name === 'TypeError' ||
        (err.message ?? '').toLowerCase().includes('failed to fetch');

      if (longRunning && isNetworkError) {
        logTransportFailure({
          event: 'long_running_network_failure',
          requestId: null,
          logicalCallId: logicalCallId || null,
          generationPolicy: policy,
          operation: operation || null,
          failurePhase: err.name === 'AbortError' ? 'client_abort' : 'network',
          generateUrl,
          attempt: attempt + 1,
          maxRetries,
        });
        if (policy === 'sequel_step') {
          throw new GenerationOutcomeUnknownError(
            'Generation request ended before a definitive result was returned. Resume the sequel setup to continue.',
            { requestId: lastRequestId, operation, agent },
          );
        }
      }

      if (policy === 'sequel_step' && isNetworkError) {
        throw new GenerationOutcomeUnknownError(
          'Generation request ended before a definitive result was returned. Resume the sequel setup to continue.',
          { requestId: lastRequestId, operation, agent },
        );
      }

      if (isNetworkError && attempt < maxRetries - 1) {
        const backoff = BASE_DELAY_MS * Math.pow(2, attempt);
        console.warn(`[geminiTransport] Network error (attempt ${attempt + 1}). Retrying in ${backoff}ms.`);
        await delay(backoff);
        continue;
      }
      throw error;
    }
  }

  throw lastError;
};

export const assertServerTransportRequired = (): void => {
  const isProd = (import.meta as any).env?.PROD === true || (import.meta as any).env?.MODE === 'production';
  const flagOff = (import.meta as any).env?.VITE_USE_SERVER_GEMINI === 'false';
  if (isProd && flagOff) {
    throw new Error(
      '[geminiTransport] VITE_USE_SERVER_GEMINI=false is not allowed in production builds. ' +
        'All Gemini calls must go through /api/generate.',
    );
  }
};

/** @internal test helper */
export const __testables = {
  resolveAgentLabel,
  getIdTokenForTests: getIdToken,
  resetLastRequestId: () => {
    lastRequestId = undefined;
  },
};
